/**
 * @module geo/middleware
 */
import { Action } from "redux";
import { Epic } from "redux-observable";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  delay,
  map,
  tap,
  ignoreElements,
  mergeMap,
  switchMap,
  concat
} from "rxjs/operators";
import {
  Action as ActionFsa,
  isType,
  AnyAction,
  Success,
  Failure
} from "typescript-fsa";

import { pendingServiceCreator } from "../actions/creators/application.creators";
import { SERVICE_DONECreator } from "../actions/creators/proxy.creators";
import {
  callServiceMethodAsyncCreator,
  callServiceMethodCreator,
  callServiceRESTAsyncCreator,
  callServiceRESTCreator,
  serviceExecuteSearchAsyncCreator,
  serviceExecuteSearchCreator,
  serviceFeatureTransactionAsyncCreator,
  serviceFeatureTransactionCreator
} from "../actions/creators/services.creator";
import busService from "../bus";
import { SERVICE_CALL_METHOD } from "../payload/facade/services/SERVICE_CALL_METHOD";
import { ofAction } from "../utils/observableepics";
import { SERVICE_CALL_REST } from "../payload/facade/services/SERVICE_CALL_REST";
import { SERVICE_EXECUTE_SEARCH } from "../payload/facade/services/SERVICE_EXECUTE_SEARCH";
import { SERVICE_FEATURE_TRANSACTION } from "../payload/facade/services/SERVICE_FEATURE_TRANSACTION";
import { ExecuteSearch } from "../types/services/execute.search";
import { FeaturesTransaction } from "../types/services/features.transaction";
import { KindService } from "../types/services/kind.service";
import { AppStore } from "../store/app";

import {
  ServiceGeoDone,
  ServiceGeoPromise,
  ServiceGeoDoneData
} from "../payload/response/service.geo.done";
import { any } from "prop-types";
import { serviceGetImageMapCreator, serviceGetImageMapAsyncCreator } from '../actions/creators/service.get.image.map.creator';
import { SERVICE_GET_IMAGE_MAP } from "../payload/facade";

/**
 * @private
 *
 */
export const serviceCallMethodEpic: Epic<Action, AppStore> = (
  action$,
  store,
  uid
) =>
  action$
    .filter(
      action =>
        isType(action, callServiceMethodCreator) &&
        KindService.CALL_METHOD === action.payload.kindService
    )
    .mergeMap(action => {
      const actionFsa = action as ActionFsa<SERVICE_CALL_METHOD>;
      return Observable.fromPromise(
        busService.geoBus.send<any>(
          callServiceMethodAsyncCreator.type,
          actionFsa.payload,
          uid
        )
      )
        .map(results => {
          const ps = { ...results.data, uid: actionFsa.payload.id };
          return pendingServiceCreator(ps);
        })
        .concat(
          action$
            .ofType(SERVICE_DONECreator.type)
            .filter((action: any) => {
              return KindService.CALL_METHOD === action.payload.type;
            })
            .take(1)
            .map((action: any) => {
              return callServiceMethodAsyncCreator.done({
                result: action.payload,
                params: actionFsa.payload
              });
            })
        )
        .catch(error =>
          Observable.of(
            callServiceMethodAsyncCreator.failed({
              params: actionFsa.payload,
              error
            })
          )
        );
    });
/**
 * @private
 *
 */
export const serviceCallRESTEpic: Epic<Action, AppStore> = (
  action$,
  store,
  uid
) =>
  action$
    .filter(action => {
      return (
        isType(action, callServiceRESTCreator) &&
        KindService.CALL_REST === action.payload.kindService
      );
    })
    .mergeMap(action => {
      const actionFsa = action as ActionFsa<SERVICE_CALL_REST>;
      return Observable.fromPromise(
        busService.geoBus.send<any>(
          callServiceRESTAsyncCreator.type,
          actionFsa.payload,
          uid
        )
      )
        .map(results => {
          const ps = { ...results.data, uid: actionFsa.payload.id };
          return pendingServiceCreator(ps);
        })
        .concat(
          action$
            .ofType(SERVICE_DONECreator.type)
            .filter((action: any) => {
              return KindService.CALL_REST === action.payload.type;
            })
            .take(1)
            .map((action: any) => {
              return callServiceRESTAsyncCreator.done({
                result: action.payload,
                params: actionFsa.payload
              });
            })
        )
        .catch(error =>
          Observable.of(
            callServiceRESTAsyncCreator.failed({
              params: actionFsa.payload,
              error
            })
          )
        );
    });

export const serviceGetImageEpic: Epic<AnyAction, AppStore> = (
      action$,
      store,
      uid
    ) =>
      action$.pipe(
        ofAction(serviceGetImageMapCreator),
        mergeMap(action => {
          const actionFsa = action as ActionFsa<SERVICE_GET_IMAGE_MAP>; // a problem with old epic version, just syntaxic sugar.
          return Observable.concat(
            of(serviceGetImageMapAsyncCreator.started(action.payload)),
            Observable.fromPromise(
              busService.geoBus.send<ServiceGeoPromise>(
                serviceGetImageMapAsyncCreator.type,
                actionFsa.payload,
                uid
              )
            )
              .mergeMap(results => {
                return Observable.fromPromise<ServiceGeoDoneData>(
                  results.data.payload.promise.promise
                ).map(resultService => {
                  const action = serviceGetImageMapAsyncCreator.done(
                    {
                      result: resultService.payload,
                      params: actionFsa.payload
                    },
                    {
                      params: actionFsa.payload,
                      transient: actionFsa.payload.transient
                    }
                  );
                   
                  return action;
                });
              })
              .catch(error => {
                return Observable.of(
                  serviceGetImageMapAsyncCreator.failed(
                    { params: actionFsa.payload, error },
                    {
                      params: actionFsa.payload,
                      transient: actionFsa.payload.transient
                    }
                  )
                );
              })
          );
        })
      );



export const serviceFeatureTransactionEpic: Epic<AnyAction, AppStore> = (
  action$,
  store,
  uid
) =>
  action$.pipe(
    ofAction(serviceFeatureTransactionCreator),
    mergeMap(action => {
      const actionFsa = action as ActionFsa<SERVICE_FEATURE_TRANSACTION>; // a problem with old epic version, just syntaxic sugar.
      return Observable.concat(
        of(serviceFeatureTransactionAsyncCreator.started(action.payload)),
        Observable.fromPromise(
          busService.geoBus.send<ServiceGeoPromise>(
            serviceFeatureTransactionAsyncCreator.type,
            actionFsa.payload,
            uid
          )
        )
          .mergeMap(results => {
            return Observable.fromPromise<ServiceGeoDoneData>(
              results.data.payload.promise.promise
            ).map(resultService => {
              delete resultService.payload.config;
              delete resultService.payload.headers;
              delete resultService.payload.objectkey;
              delete resultService.payload.status;
              const action = serviceFeatureTransactionAsyncCreator.done(
                {
                  result: resultService.payload,
                  params: actionFsa.payload
                },
                {
                  params: actionFsa.payload,
                  transient: actionFsa.payload.transient
                }
              );

              return action;
            });
          })
          .catch(error => {
            return Observable.of(
              serviceFeatureTransactionAsyncCreator.failed(
                { params: actionFsa.payload, error },
                {
                  params: actionFsa.payload,
                  transient: actionFsa.payload.transient
                }
              )
            );
          })
      );
    })
  );

export const serviceExecuteSearchEpic: Epic<AnyAction, AppStore> = (
  action$,
  store,
  uid
) =>
  action$.pipe(
    ofAction(serviceExecuteSearchCreator),
    mergeMap(action => {
      const actionFsa = action as ActionFsa<SERVICE_EXECUTE_SEARCH>; // a problem with old epic version, just syntaxic sugar.
      return Observable.concat(
        of(serviceExecuteSearchAsyncCreator.started(action.payload)),
        Observable.fromPromise(
          busService.geoBus.send<ServiceGeoPromise>(
            serviceExecuteSearchAsyncCreator.type,
            actionFsa.payload,
            uid
          )
        )
          .mergeMap(results => {
            return Observable.fromPromise<ServiceGeoDoneData>(
              results.data.payload.promise.promise
            ).map(resultService => {
              const action = serviceExecuteSearchAsyncCreator.done(
                {
                  result: resultService.payload,
                  params: actionFsa.payload
                },
                {
                  params: actionFsa.payload,
                  transient: actionFsa.payload.transient
                }
              );

              return action;
            });
          })
          .catch(error => {
            return Observable.of(
              serviceExecuteSearchAsyncCreator.failed(
                { params: actionFsa.payload, error },
                {
                  params: actionFsa.payload,
                  transient: actionFsa.payload.transient
                }
              )
            );
          })
      );
    })
  );
